import type { Client } from "stoat.js";

/**
 * Profile as returned by the TYPUS API, including the custom page fields
 */
export type TypusProfile = {
  content?: string | null;
  custom_html?: string | null;
  custom_css?: string | null;
  style?: string | null;
};

/**
 * Limits enforced by the server
 */
export const CUSTOM_HTML_LIMIT = 64_000;
export const CUSTOM_CSS_LIMIT = 32_000;

/**
 * Fetch a user's profile including the custom page fields
 * @param client Client
 * @param userId User id
 * @returns Profile
 */
export async function fetchTypusProfile(
  client: Client,
  userId: string,
): Promise<TypusProfile> {
  return (await client.api.get(
    `/users/${userId as ""}/profile`,
  )) as unknown as TypusProfile;
}

/**
 * Mark an uploaded file as used by the account so it is not cleaned up
 * (the SDK drops the body of routes it does not know, so call it directly)
 * @param client Client
 * @param id Uploaded file id
 * @param kind What the file is used for
 */
export async function claimTypusAsset(
  client: Client,
  id: string,
  kind: "profile" | "wallpaper",
) {
  const { baseURL, headers } = client.api.config;
  const response = await fetch(`${baseURL}/users/@me/assets`, {
    method: "POST",
    headers: {
      ...(headers as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id, kind }),
  });

  if (!response.ok) throw new Error(`AssetClaimFailed: ${response.status}`);
}

/**
 * Elements removed before rendering (the server already strips them,
 * this is defence in depth)
 */
const FORBIDDEN_ELEMENTS =
  "script, style, iframe, frame, object, embed, form, input, button, textarea, select, link, meta, base, svg, math, template";

/**
 * Origin of a URL, or nothing if it cannot be parsed
 * @param url URL
 * @returns Origin
 */
function originOf(url?: string) {
  try {
    return url ? new URL(url).origin : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build the isolated document for a custom profile page
 *
 * The result is meant for an `<iframe sandbox srcdoc>` without
 * `allow-scripts` or `allow-same-origin`, so it runs in an opaque origin
 * with no access to the app, its storage or the session token.
 *
 * @param client Client (used for media URLs and the image proxy)
 * @param html Sanitised HTML from the server
 * @param css Sanitised CSS from the server
 * @param themeVars CSS variables exposed to the page
 * @returns Full HTML document
 */
export function buildProfileDocument(
  client: Client,
  html: string,
  css: string,
  themeVars: Record<string, string>,
) {
  const features = client.configuration?.features;
  const mediaOrigin = originOf(features?.autumn?.url);
  const proxyOrigin = originOf(features?.january?.url);

  // parse in an inert document: nothing loads or runs here
  const doc = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html",
  );

  doc.querySelectorAll(FORBIDDEN_ELEMENTS).forEach((el) => el.remove());

  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (
        name.startsWith("on") ||
        value.startsWith("javascript:") ||
        value.startsWith("data:text")
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  // external images go through the image proxy (hides viewers' IP)
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") ?? "";
    if (originOf(src) !== mediaOrigin) {
      const proxied = client.proxyFile(src);
      if (proxied) img.setAttribute("src", proxied);
    }
    img.setAttribute("referrerpolicy", "no-referrer");
    img.setAttribute("loading", "lazy");
  });

  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });

  const allowedImages = [mediaOrigin, proxyOrigin].filter(Boolean).join(" ");
  const csp = [
    "default-src 'none'",
    `img-src ${allowedImages || "'none'"}`,
    "style-src 'unsafe-inline'",
    "font-src 'none'",
    "media-src 'none'",
    "form-action 'none'",
  ].join("; ");

  const vars = Object.entries(themeVars)
    .map(([key, value]) => `${key}:${value};`)
    .join("");

  // user CSS cannot close the style element (the server removes "<")
  const safeCss = css.replace(/</g, "");

  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"><style>:root{${vars}}html,body{margin:0;padding:0;min-height:100%;}html{height:100%;}body{box-sizing:border-box;padding:16px;font-family:ui-monospace,Menlo,Consolas,monospace;color:var(--typ-text);background:transparent;overflow-wrap:anywhere;}img{max-width:100%;}a{color:var(--typ-primary);}</style><style>${safeCss}</style></head><body>${doc.body.innerHTML}</body></html>`;
}

/**
 * Theme colours custom pages can use, e.g. `var(--typ-primary)`
 * @returns CSS variables
 */
export function profileThemeVars(): Record<string, string> {
  const style = getComputedStyle(document.body);
  const read = (name: string) => style.getPropertyValue(name).trim();

  return {
    "--typ-primary": read("--typ-color-primary"),
    "--typ-accent": read("--typ-color-tertiary"),
    "--typ-surface": read("--typ-color-surface-container"),
    "--typ-text": read("--typ-color-on-surface"),
    "--typ-muted": read("--typ-color-outline"),
  };
}

/**
 * Example page shown when the user starts from a template
 */
export const PROFILE_TEMPLATE = {
  html: `<div class="card">
  <h1>Olá! 👋</h1>
  <p>Este é o meu cantinho no <strong>TYPUS</strong>.</p>
  <div class="tags">
    <span>🎧 música</span>
    <span>💻 código</span>
    <span>🌊 oceano</span>
  </div>
  <p><a href="https://example.com">Meu portfólio</a></p>
</div>`,
  css: `.card {
  padding: 24px;
  border-radius: 20px;
  background: linear-gradient(145deg, var(--typ-primary), #11385a);
  color: #ffffff;
}
.card h1 { margin: 0 0 8px; }
.tags { display: flex; gap: 8px; flex-wrap: wrap; }
.tags span {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}
.card a { color: #ffe0db; }`,
};
